import keycloak from './keycloak';

// API URL for backend calls
const API_URL = process.env.REACT_APP_API_URL || '/api';

// Helper function to decode JWT tokens
const decodeToken = (token) => {
  if (!token) {
    return null;
  }
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (e) {
    console.error('Error decoding token:', e);
    return null;
  }
};

// Ensure user profile exists in the database after login
const ensureUserProfile = async (userId, token) => {
  try {
    const response = await fetch(`${API_URL}/profile/ensure`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ userId }),
    });

    if (!response.ok) {
      console.warn('Failed to ensure user profile:', response.status);
      return null;
    }

    const result = await response.json();
    return result;
  } catch (error) {
    console.error('Error ensuring user profile:', error);
    return null;
  }
};

export const login = async (username, password, rememberMe = false) => {
  try {
    // Call backend proxy for login instead of direct Keycloak access
    const API_URL = process.env.REACT_APP_API_URL || '/api';

    // Check if we're in development and need to point to full URL if proxy isn't set up
    // This is a fallback, ideally proxy should handle /api
    const baseUrl = API_URL.startsWith('http') ? API_URL : API_URL;

    const response = await fetch(`${baseUrl}/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        username,
        password,
        rememberMe,
      }),
    });

    if (!response.ok) {
      const error = await response.json();

      // If MFA is required, redirect to Keycloak login page
      if (error.requiresMfa || error.code === 'MFA_REQUIRED') {
        const keycloakUrl = process.env.REACT_APP_KEYCLOAK_URL || 'http://localhost:8081';
        const realm = process.env.REACT_APP_KEYCLOAK_REALM || 'ai-leadgen';
        const clientId = process.env.REACT_APP_KEYCLOAK_CLIENT_ID || 'ai-leadgen-frontend';
        const redirectUri = window.location.origin;

        const loginUrl = `${keycloakUrl}/realms/${realm}/protocol/openid-connect/auth?client_id=${clientId}&redirect_uri=${encodeURIComponent(redirectUri)}&response_type=code&scope=openid`;
        window.location.href = loginUrl;
        return false; // Don't throw, we're redirecting
      }

      throw new Error(error.message || error.error_description || 'Login failed');
    }

    const tokens = await response.json();

    // Set the tokens in Keycloak instance
    keycloak.token = tokens.access_token;
    keycloak.refreshToken = tokens.refresh_token;
    keycloak.idToken = tokens.id_token;

    // Only decode if tokens exist
    if (tokens.access_token) {
      keycloak.tokenParsed = decodeToken(tokens.access_token);
    }
    if (tokens.refresh_token) {
      keycloak.refreshTokenParsed = decodeToken(tokens.refresh_token);
    }
    if (tokens.id_token) {
      keycloak.idTokenParsed = decodeToken(tokens.id_token);
    }

    keycloak.authenticated = true;

    // Setup token refresh
    const expiresIn = tokens.expires_in || 300;
    keycloak.timeSkew = 0;

    // Start automatic token refresh
    keycloak.startTokenRefresh(expiresIn);

    // Store remember me preference
    if (rememberMe) {
      const now = Date.now();
      const thirtyDaysInMs = 30 * 24 * 60 * 60 * 1000; // 30 days in milliseconds
      const sessionExpiry = now + thirtyDaysInMs;

      localStorage.setItem('remember_me', 'true');
      localStorage.setItem('session_expiry', sessionExpiry.toString());
      // Store tokens in localStorage for persistent session
      localStorage.setItem(
        'keycloak_tokens',
        JSON.stringify({
          access_token: tokens.access_token,
          refresh_token: tokens.refresh_token,
          id_token: tokens.id_token,
          expires_at: Date.now() + expiresIn * 1000,
        })
      );
      localStorage.setItem('session_start', now.toString());
      localStorage.setItem('last_activity', now.toString());
    } else {
      localStorage.removeItem('remember_me');
      localStorage.removeItem('keycloak_tokens');
      localStorage.removeItem('session_expiry');

      // Use sessionStorage for temporary session and store tokens
      // so the session persists across page refreshes (F5)
      sessionStorage.setItem(
        'keycloak_tokens',
        JSON.stringify({
          access_token: tokens.access_token,
          refresh_token: tokens.refresh_token,
          id_token: tokens.id_token,
          expires_at: Date.now() + expiresIn * 1000,
        })
      );
      sessionStorage.setItem('session_start', Date.now().toString());
      sessionStorage.setItem('last_activity', Date.now().toString());
    }

    // Ensure user profile exists in the database (fire-and-forget, don't block login)
    const userId = keycloak.tokenParsed?.sub;
    if (userId) {
      ensureUserProfile(userId, tokens.access_token).catch((err) =>
        console.warn('Profile ensure failed (non-blocking):', err.message)
      );
    }

    // Immediately notify KeycloakContext about auth state change
    // This prevents the 3-second sync interval from causing a re-render race
    keycloak.notifyAuthSuccess();

    return true;
  } catch (error) {
    console.error('Login error:', error);
    throw error;
  }
};

export const register = (redirectUri = window.location.origin) => {
  const registerUrl = keycloak.createRegisterUrl({
    redirectUri: redirectUri,
  });
  window.location.href = registerUrl;
};

export const forgotPassword = () => {
  // Keycloak doesn't have a built-in password reset URL creator
  // We need to construct it manually
  const resetUrl = `${keycloak.authServerUrl}/realms/${keycloak.realm}/login-actions/reset-credentials?client_id=${keycloak.clientId}&redirect_uri=${encodeURIComponent(window.location.origin)}`;
  window.location.href = resetUrl;
};

export const logout = async () => {
  try {
    // Stop token refresh
    keycloak.stopTokenRefresh();

    // Revoke refresh token on server
    if (keycloak.refreshToken) {
      const keycloakUrl = process.env.REACT_APP_KEYCLOAK_URL || 'http://localhost:8081';
      const realm = process.env.REACT_APP_KEYCLOAK_REALM || 'ai-leadgen';
      const clientId = process.env.REACT_APP_KEYCLOAK_CLIENT_ID || 'ai-leadgen-frontend';

      try {
        await fetch(`${keycloakUrl}/realms/${realm}/protocol/openid-connect/logout`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: new URLSearchParams({
            client_id: clientId,
            refresh_token: keycloak.refreshToken,
          }),
        });
      } catch (error) {
        console.error('Error revoking tokens:', error);
      }
    }
  } catch (error) {
    console.error('Logout error:', error);
  }

  // Clear tokens from Keycloak instance
  keycloak.token = null;
  keycloak.refreshToken = null;
  keycloak.idToken = null;
  keycloak.tokenParsed = null;
  keycloak.refreshTokenParsed = null;
  keycloak.idTokenParsed = null;
  keycloak.authenticated = false;

  // Clear all storage
  localStorage.removeItem('userId');
  localStorage.removeItem('username');
  localStorage.removeItem('email');
  localStorage.removeItem('isAdmin');
  localStorage.removeItem('keycloak_tokens');
  localStorage.removeItem('remember_me');
  localStorage.removeItem('session_expiry');
  localStorage.removeItem('session_start');
  localStorage.removeItem('last_activity');

  // Clear prospect finder cache
  localStorage.removeItem('lusha_prospects_cache');
  localStorage.removeItem('lusha_companies_cache');

  sessionStorage.removeItem('session_start');
  sessionStorage.removeItem('last_activity');

  // Clear all session storage
  sessionStorage.clear();

  // Redirect to login page
  window.location.href = '/login';
};

export const getCurrentUser = () => {
  if (!keycloak.authenticated) {
    return null;
  }

  const roles = keycloak.tokenParsed?.realm_access?.roles || [];

  // Determine highest role
  let highestRole = 'user';
  if (roles.includes('superadmin')) {
    highestRole = 'superadmin';
  } else if (roles.includes('admin')) {
    highestRole = 'admin';
  }

  // Get organization from token attributes or localStorage
  const org =
    keycloak.tokenParsed?.organization?.[0] || localStorage.getItem('currentOrg') || 'Unassigned';

  return {
    userId: keycloak.tokenParsed?.sub,
    username: (
      keycloak.tokenParsed?.preferred_username ||
      keycloak.tokenParsed?.email ||
      'user'
    ).includes('@')
      ? (keycloak.tokenParsed?.preferred_username || keycloak.tokenParsed?.email)
          .split('@')[0]
          .replace(/[^a-z0-9_]/g, '')
          .toLowerCase()
      : keycloak.tokenParsed?.preferred_username || 'user',
    email: keycloak.tokenParsed?.email,
    firstName: keycloak.tokenParsed?.given_name,
    lastName: keycloak.tokenParsed?.family_name,
    roles: roles,
    role: highestRole,
    isSuperadmin: roles.includes('superadmin'),
    isAdmin: roles.includes('admin') || roles.includes('superadmin'),
    isUser: roles.includes('user'),
    organization: org,
    currentOrg: localStorage.getItem('currentOrg') || org,
  };
};

// Get current organization context (for API calls)
export const getOrgContext = () => {
  const user = getCurrentUser();
  if (!user) return null;

  // Superadmin can switch orgs, others use their own org
  if (user.isSuperadmin) {
    return localStorage.getItem('currentOrg') || 'all';
  }

  return user.organization;
};

// Check if current user has specific role
export const hasRole = (role) => {
  const user = getCurrentUser();
  return user?.roles?.includes(role) || false;
};

export const getToken = () => {
  return keycloak.token;
};

export const getKeycloak = () => {
  return keycloak;
};

// Check if user has MFA enabled
export const checkMfa = async (username, password, rememberMe = false) => {
  try {
    const API_URL = process.env.REACT_APP_API_URL || '/api';

    const response = await fetch(`${API_URL}/auth/check-mfa`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        username,
        password,
        rememberMe,
      }),
    });

    const result = await response.json();
    return result;
  } catch (error) {
    console.error('Error checking MFA:', error);
    throw error;
  }
};

// Login with OTP (second step for MFA)
export const loginWithOtp = async (username, password, otp, rememberMe = false) => {
  try {
    const API_URL = process.env.REACT_APP_API_URL || '/api';

    const response = await fetch(`${API_URL}/auth/login/otp`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        username,
        password,
        otp,
        rememberMe,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'OTP verification failed');
    }

    const tokens = await response.json();

    // Set the tokens in Keycloak instance
    keycloak.token = tokens.access_token;
    keycloak.refreshToken = tokens.refresh_token;
    keycloak.idToken = tokens.id_token;

    // Only decode if tokens exist
    if (tokens.access_token) {
      keycloak.tokenParsed = decodeToken(tokens.access_token);
    }
    if (tokens.refresh_token) {
      keycloak.refreshTokenParsed = decodeToken(tokens.refresh_token);
    }
    if (tokens.id_token) {
      keycloak.idTokenParsed = decodeToken(tokens.id_token);
    }

    keycloak.authenticated = true;

    // Setup token refresh
    const expiresIn = tokens.expires_in || 300;
    keycloak.timeSkew = 0;

    // Start automatic token refresh
    keycloak.startTokenRefresh(expiresIn);

    // Store remember me preference
    if (rememberMe) {
      const now = Date.now();
      const thirtyDaysInMs = 30 * 24 * 60 * 60 * 1000; // 30 days in milliseconds
      const sessionExpiry = now + thirtyDaysInMs;

      localStorage.setItem('remember_me', 'true');
      localStorage.setItem('session_expiry', sessionExpiry.toString());
      // Store tokens in localStorage for persistent session
      localStorage.setItem(
        'keycloak_tokens',
        JSON.stringify({
          access_token: tokens.access_token,
          refresh_token: tokens.refresh_token,
          id_token: tokens.id_token,
          expires_at: Date.now() + expiresIn * 1000,
        })
      );
      localStorage.setItem('session_start', now.toString());
      localStorage.setItem('last_activity', now.toString());
    } else {
      localStorage.removeItem('remember_me');
      localStorage.removeItem('keycloak_tokens');
      localStorage.removeItem('session_expiry');

      // Use sessionStorage for temporary session and store tokens
      // so the session persists across page refreshes (F5)
      sessionStorage.setItem(
        'keycloak_tokens',
        JSON.stringify({
          access_token: tokens.access_token,
          refresh_token: tokens.refresh_token,
          id_token: tokens.id_token,
          expires_at: Date.now() + expiresIn * 1000,
        })
      );
      sessionStorage.setItem('session_start', Date.now().toString());
      sessionStorage.setItem('last_activity', Date.now().toString());
    }

    // Ensure user profile exists in the database
    const userId = keycloak.tokenParsed?.sub;
    if (userId) {
      await ensureUserProfile(userId, tokens.access_token);
    }

    return true;
  } catch (error) {
    console.error('OTP login error:', error);
    throw error;
  }
};

export default {
  login,
  loginWithOtp,
  checkMfa,
  register,
  forgotPassword,
  logout,
  getCurrentUser,
  hasRole,
  getToken,
  getKeycloak,
};
